import React, { FC } from 'react';
import {
  CatHeading,
  CatTable,
  CatTableBody,
  CatTableCell,
  CatTableHeader,
  CatTableHeaderCell,
  CatTableObject,
  CatTableRow,
  CatTextLink
} from 'components/blocks-components';
import { useTranslations } from 'next-intl';
import { getPartsDetails } from 'services/parts-diagram/actions';
import useURLParams from 'hooks/useURLParams';
import MultipleSystemLink from '../../../multiple-system-link';
import styles from './list-of-arrangements.module.scss';
import { usePartDiagram } from 'hooks/use-part-diagram/parts-diagram-context';
import {
  CATEGORY_TYPES,
  GroupItemType,
  ListOfArrangementsResponse
} from 'types/parts-diagram';

export interface ListOfArrangementsProps {
  listOfArrangementsData: ListOfArrangementsResponse;
}

const ListOfArrangements: FC<ListOfArrangementsProps> = ({
  listOfArrangementsData
}) => {
  const t = useTranslations();
  const { handlePartsContentCategory, handleSelectedPart } = usePartDiagram();
  const { setParamsWithWindows, deleteParams, checkParamsHas } = useURLParams();
  const tableHeaderData = [{ Id: 1, Header: t('PARTNUMBER_NAME') }];

  const tableBodyData = listOfArrangementsData?.lineItems.map((lineItem) => ({
    partNumber: lineItem.partNumber,
    name: lineItem.name,
    ieSystemControlNumber: lineItem.ieSystemControlNumber,
    disambiguation: lineItem.disambiguation,
    attachmentSerialNumber: lineItem.attachmentSerialNumber,
    componentId: lineItem.componentId
  }));

  const handlePartsDetails = async (groupItem: GroupItemType) => {
    handlePartsContentCategory(CATEGORY_TYPES.LOADER);
    const {
      ieSystemControlNumber = '',
      componentId = '',
      partNumber = ''
    } = groupItem || {};
    checkParamsHas('systemId') && deleteParams('systemId');
    setParamsWithWindows('componentId', componentId);
    setParamsWithWindows('ieSystemControlNumber', ieSystemControlNumber);
    const { partDetailsAndSchematicResponse } = await getPartsDetails(
      ieSystemControlNumber
    );

    if (partDetailsAndSchematicResponse) {
      handlePartsContentCategory(
        CATEGORY_TYPES.PARTSDETAILS,
        null,
        partDetailsAndSchematicResponse
      );
      handleSelectedPart({ partNumber, ieSystemControlNumber });
    }
  };

  return (
    <CatTableObject height="auto">
      <div slot="header" className="cat-u-padding-bottom-md">
        <CatHeading variant="title">
          {`${t('PARTS_DIAG_PRODUCT_CONFIGURATION')} (${listOfArrangementsData?.serialNumber})`}
        </CatHeading>
      </div>
      <CatTable
        /* @ts-expect-error Modifying a var from blocks to fix the default high z-index of the table header cell */ style={{
          '--cat-table-header-cell-zindex-pass-down': '10'
        }}
      >
        <CatTableHeader>
          <CatTableRow>
            {tableHeaderData.map((item) => {
              return (
                <CatTableHeaderCell
                  key={item.Id}
                  className={styles['table_padding_remove']}
                >
                  {item.Header}
                </CatTableHeaderCell>
              );
            })}
          </CatTableRow>
        </CatTableHeader>

        <CatTableBody>
          {tableBodyData.map((item) => {
            if (
              item.attachmentSerialNumber ||
              (!item.disambiguation && item.ieSystemControlNumber === null)
            ) {
              return (
                <CatTableRow key={item.partNumber}>
                  <CatTableCell className={styles['table_padding_remove']}>
                    {`${item.partNumber} : ${item.name}`}
                  </CatTableCell>
                </CatTableRow>
              );
            }
            if (item.disambiguation && item.ieSystemControlNumber === null) {
              return (
                <MultipleSystemLink
                  partNumber={item.partNumber}
                  name={item.name}
                  key={item.partNumber}
                />
              );
            }
            return (
              <CatTableRow key={item.partNumber}>
                <CatTableCell className={styles['table_padding_remove']}>
                  <CatTextLink
                    size="sm"
                    onClick={(e) => handlePartsDetails(item)}
                  >
                    {`${item.partNumber} : ${item.name}`}
                  </CatTextLink>
                </CatTableCell>
              </CatTableRow>
            );
          })}
        </CatTableBody>
      </CatTable>
    </CatTableObject>
  );
};

export default ListOfArrangements;
